/** @jsxImportSource @emotion/react */
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  DialogActions,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { GreyButton, GreyLineButton, ic } from 'components/buttons/CustomButton';
import { CustomInputText, CustomTextarea } from 'components/inputs/CustomInput';
import { ContentGrid } from 'components/layouts/ContentGrid';
import {
  FlexBetween,
  ContentSection,
  GridInfoSection,
  GridInfo,
  GridButtons,
} from 'components/layouts/ContentSection';
import { di } from 'components/layouts/Dialog';
import { ContainerLayout } from 'components/layouts/MainLayout';
import { SearchBox, SearchRows, SearchButtons } from 'components/layouts/SearchBox';
import { ViewTable } from 'components/tables/ViewTable';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import BasicDatePicker from 'components/inputs/DatePicker';
import { Pagination, PaginationRef } from 'components/layouts/Pagination';
import dayjs, { Dayjs } from 'dayjs';
import { IfLog, IfLogRequest } from 'models/admin/IfLog';
import { PaginationResponse } from 'models/common/Pagination';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { findIfLogs } from 'apis/admin/ifLog';
import useSessionStore from 'stores/useSessionStore';
import { downLoadIFLogListExcel } from 'apis/admin/LogExcelDownload';
import { ExcelDownloadRequest } from 'models/common/Excel';
import SearchMolecule from '../../components/common/SearchMolecule';

type DialogProps = {
  open: boolean;
  ifDtm: string;
  divsNm: string;
  content: string;
  result: string;
};

const IfLogManagementPage = () => {
  const { t } = useTranslation();
  const { gridNoRowsTemplate } = useSessionStore();

  const pageRef = useRef<PaginationRef>(null);
  const [ifLogList, setIfLogList] = useState<PaginationResponse<IfLog>>({
    totalCount: 0,
    list: [],
  });

  const [searchCondition, setSearchCondition] = useState<IfLogRequest>({
    ifLogDtmSt: dayjs().add(-1, 'month').format('YYYYMMDD').toString(),
    ifLogDtmEnd: dayjs().format('YYYYMMDD').toString(),
    searchItem: '',
    searchNm: '',
  });

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [dialogProp, setDialogProp] = useState<DialogProps>({
    open: false,
    ifDtm: '',
    divsNm: '',
    content: '',
    result: '',
  });

  useEffect(() => {
    getIfLogs();
  }, []);

  const handleExcelDownload = async () => {
    const execelData: ExcelDownloadRequest<IfLogRequest> = {
      fileName: '인터페이스로그.xlsx',
      sheetName: '인터페이스로그',
      header: [
        t('if-log.column.발생 일시', '__발생 일시'),
        t('if-log.column.송/수신구분', '__송/수신구분'),
        t('if-log.column.I/F명', '__I/F명'),
        t('if-log.column.I/F 송신내용', 'I/F 송신내용'),
        t('if-log.column.I/F 수신내용', 'I/F 수신내용'),
        t('if-log.column.부가정보1', '__부가정보1'),
        t('if-log.column.부가정보2', '__부가정보2'),
        t('if-log.column.부가정보3', '__부가정보3'),
        t('if-log.column.부가정보4', '__부가정보4'),
        t('if-log.column.부가정보5', '__부가정보5'),
      ],
      //과부하 방지 10000건만 출력
      searchCondition: { ...searchCondition, pageSize: '10000', start: '0' },
    };
    downLoadIFLogListExcel(execelData);
  };

  const getIfLogs = async (search?: boolean, condition?: IfLogRequest) => {
    const response = await findIfLogs(condition ?? searchCondition);
    if (response) {
      setIfLogList(response);
      if (search) {
        setPageNo(1);
        pageRef.current?.setSelectNo(1);
      }
    }
  };

  const handleSearch = () => {
    const condition: IfLogRequest = { ...searchCondition, start: '0' };

    setSearchCondition(condition);
    getIfLogs(true, condition);
  };

  const handleClickOpen = (data: IfLog | undefined) => {
    if (typeof data === 'undefined') return;
    setDialogProp({
      open: true,
      ifDtm: data.ifLogDtm,
      divsNm: data.cmnCdNm,
      content: data.ifTrmtValCtn,
      result: data.ifRestValCtn,
    });
  };
  const handleClose = () => {
    setDialogProp({
      open: false,
      divsNm: '',
      ifDtm: '',
      content: '',
      result: '',
    });
  };

  const defaultColum: ColDef = {
    cellStyle: { textAlign: 'left' },
    resizable: true,
  };

  const handleCrtDateChange = (newDate, id) => {
    handleDateRangeChange(newDate, id, 'ifLogDtm', 'YYYY.MM.DD');
  };

  const handleDateRangeChange = (newDate, id, key, format) => {
    const newParams = {};
    if (id === 'end') {
      newParams[key + 'End'] = newDate.format(format);

      if (
        searchCondition[key + 'St'] === '' ||
        newDate.isBefore(dayjs(searchCondition[key + 'St']))
      )
        newParams[key + 'St'] = newDate.format(format);

      setSearchCondition((prev) => {
        return {
          ...prev,
          ...newParams,
        };
      });
    } else if (id === 'start') {
      newParams[key + 'St'] = newDate.format(format);

      if (
        searchCondition[key + 'End'] === '' ||
        newDate.isAfter(dayjs(searchCondition[key + 'End']))
      )
        newParams[key + 'End'] = newDate.format(format);

      setSearchCondition((prev) => {
        return {
          ...prev,
          ...newParams,
        };
      });
    }
  };

  const handleChangePageSize = (pageSize: string) => {
    const condition: IfLogRequest = {
      ...searchCondition,
      pageSize: pageSize,
      start: '0',
    };
    setSearchCondition(condition);
    getIfLogs(false, condition);
    setPageSize(parseInt(pageSize));
    pageRef.current?.setPageSize(parseInt(pageSize));
  };
  const handleClickPageNo = (pageNo: number) => {
    const condition: IfLogRequest = {
      ...searchCondition,
      start: String(parseInt(searchCondition.pageSize ?? '10') * (pageNo - 1)),
    };
    setSearchCondition(condition);
    getIfLogs(false, condition);
    pageRef.current?.setSelectNo(pageNo);
  };
  const [columnDefs] = useState<ColDef[]>([
    {
      headerName: String(t('if-log.column.발생 일시', '__발생 일시')),
      field: 'ifLogDtm',
      cellStyle: { textAlign: 'center' },
      width: 200,
    },

    {
      headerName: String(t('if-log.column.송/수신구분', '__송/수신구분')),
      field: 'cmnCdNm',
      cellStyle: { textAlign: 'center' },
      width: 150,
    },
    {
      headerName: String(t('if-log.column.I/F명', '__I/F명')),
      field: 'ifNm',
      cellStyle: { textAlign: 'left' },
      width: 250,
    },
    {
      headerName: String(t('if-log.column.I/F 내용', '__I/F 내용')),
      field: 'ifTrmtValCtn',
      width: 250,
    },
    {
      headerName: String(t('if-log.column.I/F 결과', '__I/F 결과')),
      field: 'ifRestValCtn',
      width: 250,
      cellRenderer: (params: ICellRendererParams<IfLog>) => {
        return (
          <FlexBetween style={{ width: '100%' }}>
            <span style={{ width: '90%', overflowX: 'hidden' }}>{params.value}</span>...
            <IconButton onClick={() => handleClickOpen(params.data)}>
              <img src="/assets/icon/ic-popup.svg" alt="팝업 열기" />
            </IconButton>
          </FlexBetween>
        );
      },
    },
    {
      headerName: String(t('if-log.column.부가정보1', '__부가정보1')),
      field: 'optValNm1',
      width: 150,
    },
    {
      headerName: String(t('if-log.column.부가정보2', '__부가정보2')),
      field: 'optValNm2',
      width: 150,
    },
    {
      headerName: String(t('if-log.column.부가정보3', '__부가정보3')),
      field: 'optValNm3',
      width: 150,
    },
    {
      headerName: String(t('if-log.column.부가정보4', '__부가정보4')),
      field: 'optValNm4',
      width: 150,
    },
    {
      headerName: String(t('if-log.column.부가정보5', '__부가정보5')),
      field: 'optValNm5',
      width: 150,
    },
  ]);

  return (
    <>
      <ContainerLayout>
        <SearchBox>
          <SearchRows className="contentStart">
            <div className="searchCol">
              <SearchMolecule
                type="calendar"
                labelTitle={t('com.label.srchPeriod', '__조회기간')}
                isRequired={true}
                calendarDouble={true}
                calendarFormat="YYYY.MM.DD"
                calendarViews={['year', 'month', 'day']}
                calendarMaxWidth="128px"
                startDate={dayjs(searchCondition.ifLogDtmSt)}
                endDate={dayjs(searchCondition.ifLogDtmEnd)}
                onChange={handleCrtDateChange}
                mindate={'2024.01.01'}
                maxdate={`${String(Number(new Date().getFullYear()) + 10)}.01.01`}
              />
            </div>
            <div className="searchCol">
              <label>{t('if-log.label.I/F 내용', '__I/F 내용')}</label>
              <CustomInputText
                onChange={(e) =>
                  setSearchCondition({ ...searchCondition, searchItem: e.target.value })
                }
              />
            </div>
            {/* 인터페이스 로그 조회 화면에 인터페이스명 라이크 검색 추가 */}
            <div className="searchCol">
              <label>{t('if-log.column.I/F명', '__I/F명')}</label>
              <CustomInputText
                onChange={(e) =>
                  setSearchCondition({ ...searchCondition, searchNm: e.target.value })
                }
              />
            </div>
          </SearchRows>
          <SearchButtons className="searchButtons">
            <GreyButton className="buttonSrch" onClick={handleSearch}>
              <SearchIcon /> {t('com.label.00116', '__조회')}
            </GreyButton>
          </SearchButtons>
        </SearchBox>
        <ContentSection>
          <GridInfoSection>
            <GridInfo>
              <span>{t('if-log.label.I/F 로그목록', '__I/F 로그목록')}</span>
              <span className="primary">
                {t('com.label.tot', '__총')} {ifLogList.totalCount}
                {t('com.label.case', '__건')}
              </span>
            </GridInfo>
            <GridButtons>
              <GreyLineButton className="small" onClick={handleExcelDownload}>
                <img src="/assets/icon/ic-download.png" alt="" css={ic.icon} />
                <span>{t('com.label.00666', '__다운로드')}</span>
              </GreyLineButton>
            </GridButtons>
          </GridInfoSection>
          <ContentGrid className="ag-theme-alpine pagination">
            <AgGridReact
              overlayNoRowsTemplate={gridNoRowsTemplate}
              rowData={ifLogList.list}
              columnDefs={columnDefs}
              suppressPaginationPanel={true}
              defaultColDef={defaultColum}
            ></AgGridReact>
          </ContentGrid>
          <Pagination
            ref={pageRef}
            totalCount={ifLogList.totalCount}
            onChangePageSize={handleChangePageSize}
            onClickPageNo={handleClickPageNo}
            defaultPageNo={pageNo}
            defaultPageSize={pageSize}
          />
        </ContentSection>
      </ContainerLayout>
      <Dialog open={dialogProp.open} onClose={handleClose} css={di.dialog}>
        <DialogTitle className="popupTitle">
          {t('if-log.title.I/F 내용/결과', '__I/F 내용/결과')}
          <IconButton className="buttonClose" onClick={handleClose}>
            <CloseIcon fontSize="large"></CloseIcon>
          </IconButton>
        </DialogTitle>
        <DialogContent className="popupContent">
          <FormControl>
            <ViewTable>
              <colgroup>
                <col width="30%" />
                <col />
              </colgroup>
              <tbody>
                <tr>
                  <th scope="row">
                    <label>{t('if-log.label.발생일시', '__발생일시')}</label>
                  </th>
                  <td>
                    <CustomInputText value={dialogProp.ifDtm} disabled></CustomInputText>
                  </td>
                </tr>
                <tr>
                  <th scope="row">
                    <label>{t('if-log.label.송/수신 구분', '__송/수신 구분')}</label>
                  </th>
                  <td>
                    <CustomInputText value={dialogProp.divsNm} disabled></CustomInputText>
                  </td>
                </tr>
                <tr>
                  <th scope="row">
                    <label>{t('if-log.label.I/F 내용', '__I/F 내용')}</label>
                  </th>
                  <td>
                    <CustomTextarea value={dialogProp.content} disabled></CustomTextarea>
                  </td>
                </tr>
                <tr>
                  <th scope="row">
                    <label>{t('if-log.label.I/F 결과', '__I/F 결과')}</label>
                  </th>
                  <td>
                    <CustomTextarea value={dialogProp.result} disabled></CustomTextarea>
                  </td>
                </tr>
              </tbody>
            </ViewTable>
          </FormControl>
        </DialogContent>
        <DialogActions className="popupBottom">
          <GreyLineButton onClick={handleClose}>{t('com.label.mgt.1053', '__닫기')}</GreyLineButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IfLogManagementPage;
